/* eslint-disable react/require-default-props */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMessageContext } from 'src/context/MessageContext';
import {
  FAQ_PATH,
  INBOX_PATH,
  MESSAGE_CENTER_INBOX_PATH,
  DOCUMENT_CENTER_PATH,
  DOCUMENT_PATH,
  FAQ_FROM_NAV_LINK_PATH,
  INBOX_FROM_NAV_LINK_PATH,
  DOCUMENT_CENTER_FROM_NAV_LINK_PATH,
} from 'src/constants/routePaths';
import { getCustomerProfileLocalStorage } from 'src/utility/Utilities';
import { ListUi } from '@vwfs-bronson/bronson-react';
import { SOURCE_ACTION, useDialogContext } from 'src/context/DialogContext';
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';
import LinkItem from './LinkItem';
import globalConstants from '../../../../constants/globalConstants';
import PaperlessEnrollmentNotification from '../paperless-enrollment-notification/PaperlessEnrollmentNotification';
import BackLink from './BackLink';

interface AsideBarProps {
  brand: string;
  country: string;
}

const AsideBar: React.FC<AsideBarProps> = ({ brand = globalConstants.DEFAULT_BRAND, country }: AsideBarProps) => {
  const location = useLocation();
  const { newmessageCount } = useMessageContext();
  const { state: dialogState, showDialog } = useDialogContext();

  const { pathname } = location;

  const { isPaperless } = getCustomerProfileLocalStorage();

  const isAudiBrand = brand.toLowerCase() === globalConstants.AUDI_BRAND;

  const showBackButton = (() => {
    // asidebar is used as page so back button is controlled by brand
    if (pathname.includes(`support-center`)) return false;
    return true;
  })();

  const hasUnsavedContent = dialogState.isUpdated;

  return (
    <div data-testid='desktop-sidebar' className='u-mh u-mt' role='navigation' aria-label='main navigation'>
      {showBackButton && isAudiBrand && (
        <div id='backLinkContainer' className='u-mb'>
          <BackLink
            data-testid='back-link'
            label={i18n.t(rk.BACK_LINK)}
            brand={isAudiBrand ? globalConstants.AUDI_BRAND : globalConstants.VW_BRAND}
          />
        </div>
      )}

      <h5 className={isAudiBrand ? 'u-mb' : 'u-mb-small'}>
        {i18n.t(rk.MESSAGECENTER_SUPPORT_COMPONENT_NAME).toString()}
      </h5>
      <ListUi className='c-list-ui__custom'>
        <ListUi.Item>
          <LinkItem
            data-testid='link-item-faqs'
            label={i18n.t(rk.FAQ_COMPONENT_NAME)}
            to={hasUnsavedContent ? undefined : FAQ_PATH}
            onClick={
              hasUnsavedContent
                ? () => showDialog(SOURCE_ACTION.NAVIGATION_ITEM, { to: FAQ_FROM_NAV_LINK_PATH })
                : undefined
            }
            active={pathname.includes(`FAQs`)}
            badge={0}
            brand={brand}
          />
        </ListUi.Item>
        <ListUi.Item>
          <LinkItem
            data-testid='link-item-message-center'
            label={i18n.t(rk.MESSAGECENTER_COMPONENT_NAME)}
            to={hasUnsavedContent ? undefined : INBOX_PATH}
            onClick={
              hasUnsavedContent
                ? () => showDialog(SOURCE_ACTION.NAVIGATION_ITEM, { to: INBOX_FROM_NAV_LINK_PATH })
                : undefined
            }
            active={pathname.includes(`${MESSAGE_CENTER_INBOX_PATH}`)}
            badge={newmessageCount}
            brand={brand}
          />
        </ListUi.Item>
        {country === globalConstants?.USA && (
          <ListUi.Item>
            <LinkItem
              data-testid='link-item-doc-center'
              label={i18n.t(rk.DOCUMENT_CENTER_TITLE)}
              to={hasUnsavedContent ? undefined : DOCUMENT_PATH}
              onClick={
                hasUnsavedContent
                  ? () => showDialog(SOURCE_ACTION.NAVIGATION_ITEM, { to: DOCUMENT_CENTER_FROM_NAV_LINK_PATH })
                  : undefined
              }
              active={pathname.includes(`${DOCUMENT_CENTER_PATH}`)}
              brand={brand}
              badge={0}
            />
          </ListUi.Item>
        )}
      </ListUi>
      {country === globalConstants?.USA && !isPaperless && pathname.includes(`${DOCUMENT_CENTER_PATH}`) && (
        <PaperlessEnrollmentNotification />
      )}
    </div>
  );
};

export default AsideBar;
